import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DeepMap } from 'react-hook-form';
import { SelectOptionElement } from '@odin-labs/components';
import slugify from 'slugify';
import {
  JobsiteFeaturesModuleInput,
  JobsiteUpdateInput,
} from 'apollo/generated/client-operations';
import { ensureNonUndefinedFields } from 'utils';
import {
  CustomValidateResult,
  FormInputTypes,
  getUpdateInputValueFunction,
  GridColSpan,
  TypedFormInputs,
  UseFormMethods,
  UseInputs,
} from 'components/form';
import { toggleBorderClasses } from 'containers/jobsiteConfiguration/helpers/utils';
import { EditJobsiteConfigurationFormData } from 'containers/jobsiteConfiguration/types';
import { Jobsite } from './types';

export type FeaturesSectionInputsArgs = {
  jobsite: Jobsite;
  isSchedulerRequired: boolean;
  schedulerOptions: SelectOptionElement[];
};

export type FeaturesSectionInputsHookArgs = Omit<
  FeaturesSectionInputsArgs,
  'isSchedulerRequired'
>;

function SiteLayoutLink(): React.ReactElement {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const siteLayoutPath = `/${pathSegments.slice(0, -1).join('/')}/site-layout`;

  return (
    <Link to={siteLayoutPath} className="odin-text-odin-primary odin-underline">
      Site Layout
    </Link>
  );
}

const getFeaturesSectionInputs = (
  args: FeaturesSectionInputsArgs,
): TypedFormInputs<EditJobsiteConfigurationFormData['features']> => {
  const { jobsite, isSchedulerRequired, schedulerOptions } = args;

  const mobileAppCheckInFeatureValidation = jobsite?.isTemplate
    ? undefined
    : (value: boolean): CustomValidateResult => {
        if (value && !jobsite.appCheckInModule?.areas?.length) {
          return (
            <span>
              In order to enable this feature, at least one Site Area is
              required in the <SiteLayoutLink /> section.
            </span>
          );
        }
        // If no check-in features are enabled, we don't need to validate site areas
        return true;
      };

  return {
    announcementsEnabled: {
      element: FormInputTypes.Toggle,
      label: 'Enable Announcements',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { toggleAlignment: 'right-with-space' },
    },
    archivingAccessEventsEnabled: {
      element: FormInputTypes.Toggle,
      label: 'Enable Archiving Access Events',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { toggleAlignment: 'right-with-space' },
    },
    scheduling: {
      element: FormInputTypes.Panel,
      layout: [GridColSpan.SpanFull],
      children: {
        schedulingEnabled: {
          element: FormInputTypes.Toggle,
          label: 'Enable Scheduling',
          layout: [GridColSpan.SpanFull],
          elementProps: { toggleAlignment: 'right-with-space' },
        },
        schedulerAccountId: {
          element: FormInputTypes.Select,
          label: 'Assigned Scheduler',
          layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
          elementProps: {
            options: schedulerOptions ?? [],
            placeholder: 'Select a scheduler',
          },
          validation: isSchedulerRequired
            ? {
                required: true,
              }
            : undefined,
        },
      },
    },
    dailyLog: {
      element: FormInputTypes.Panel,
      layout: [GridColSpan.SpanFull],
      children: {
        formsEnabled: {
          element: FormInputTypes.Toggle,
          label: 'Enable Forms',
          layout: [GridColSpan.SpanFull],
          elementProps: { toggleAlignment: 'right-with-space' },
        },
        addWorkersToDailyLog: {
          element: FormInputTypes.Toggle,
          label: 'Add Workers to Daily Log',
          layout: [GridColSpan.SpanFull],
        },
      },
    },
    doNotAllowToSkipSelfOnboardingSteps: {
      element: FormInputTypes.Toggle,
      label: 'Remove Ability to Skip Steps in Self Onboarding',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { toggleAlignment: 'right-with-space' },
    },
    enforceValidContractorInSelfOnboarding: {
      element: FormInputTypes.Toggle,
      label: 'Enforce Valid Contractor in Self Onboarding',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { toggleAlignment: 'right-with-space' },
    },
    workerAppEnabled: {
      element: FormInputTypes.Toggle,
      label: 'Show Mobile App Download in Self-Onboarding',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { toggleAlignment: 'right-with-space' },
    },
    workerAppSelfCheckInEnabled: {
      element: FormInputTypes.Toggle,
      label: 'Enable Worker App Self Check In',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { toggleAlignment: 'right-with-space' },
      validation: { validate: mobileAppCheckInFeatureValidation },
    },
    workerAppAdminCheckInEnabled: {
      element: FormInputTypes.Toggle,
      label: 'Enable Worker App Admin Check In',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { toggleAlignment: 'right-with-space' },
      validation: { validate: mobileAppCheckInFeatureValidation },
    },
    workerAppBadgeScanningEnabled: {
      element: FormInputTypes.Toggle,
      label: 'Enable Worker App Badge Scanning',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { toggleAlignment: 'right-with-space' },
      validation: { validate: mobileAppCheckInFeatureValidation },
    },
    workerAppKioskCheckInEnabled: {
      element: FormInputTypes.Toggle,
      label: 'Enable Kiosk Access Events',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { toggleAlignment: 'right-with-space' },
      validation: { validate: mobileAppCheckInFeatureValidation },
    },
    selfOnboardingAutoCompleteEnabled: {
      element: FormInputTypes.Toggle,
      label: 'Enable Auto Complete for self-onboarding',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { toggleAlignment: 'right-with-space' },
    },
    workerAppKioskFrontCameraEnabled: {
      element: FormInputTypes.Toggle,
      label: 'Enable front facing camera in kiosk mode',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { toggleAlignment: 'right-with-space' },
    },
  };
};

export const getFeaturesSectionInputsHook =
  (
    args: FeaturesSectionInputsHookArgs,
  ): UseInputs<EditJobsiteConfigurationFormData['features']> =>
  (
    form: UseFormMethods<EditJobsiteConfigurationFormData['features']>,
  ): TypedFormInputs<EditJobsiteConfigurationFormData['features']> => {
    const { watch } = form;
    const { jobsite, schedulerOptions } = args;

    const isSchedulerRequired = watch<string, boolean>(
      'features.scheduling.schedulingEnabled',
    );

    return React.useMemo(() => {
      return getFeaturesSectionInputs({
        jobsite,
        isSchedulerRequired,
        schedulerOptions,
      });
    }, [jobsite, isSchedulerRequired, schedulerOptions]);
  };

// comment to trigger build for release/v2024.6
export const getFeaturesSectionDefaultValues = ({
  jobsite,
  schedulerOptions,
}: {
  jobsite: Jobsite;
  schedulerOptions: SelectOptionElement[];
}): EditJobsiteConfigurationFormData['features'] => {
  const {
    announcementsEnabled,
    archivingAccessEventsEnabled,
    formsEnabled,
    addWorkersToDailyLog,
    doNotAllowToSkipSelfOnboardingSteps,
    enforceValidContractorInSelfOnboarding,
    schedulingEnabled,
    workerAppEnabled,
    workerAppSelfCheckInEnabled,
    workerAppAdminCheckInEnabled,
    workerAppBadgeScanningEnabled,
    workerAppKioskCheckInEnabled,
    selfOnboardingAutoCompleteEnabled,
    workerAppKioskFrontCameraEnabled,
  } = jobsite?.featuresModule ?? {};

  const schedulerAccountId = jobsite?.schedulerAccount?.userAccountId;

  return {
    announcementsEnabled: !!announcementsEnabled,
    archivingAccessEventsEnabled: !!archivingAccessEventsEnabled,
    dailyLog: {
      formsEnabled: !!formsEnabled,
      addWorkersToDailyLog: !!addWorkersToDailyLog,
    },
    doNotAllowToSkipSelfOnboardingSteps: !!doNotAllowToSkipSelfOnboardingSteps,
    enforceValidContractorInSelfOnboarding:
      !!enforceValidContractorInSelfOnboarding,
    scheduling: {
      schedulingEnabled: !!schedulingEnabled,
      schedulerAccountId:
        schedulerOptions?.find(
          (option) => option.value === schedulerAccountId,
        ) ?? null,
    },
    workerAppEnabled: !!workerAppEnabled,
    workerAppSelfCheckInEnabled: !!workerAppSelfCheckInEnabled,
    workerAppAdminCheckInEnabled: !!workerAppAdminCheckInEnabled,
    workerAppBadgeScanningEnabled: !!workerAppBadgeScanningEnabled,
    workerAppKioskCheckInEnabled: !!workerAppKioskCheckInEnabled,
    selfOnboardingAutoCompleteEnabled: !!selfOnboardingAutoCompleteEnabled,
    workerAppKioskFrontCameraEnabled: !!workerAppKioskFrontCameraEnabled,
  };
};

type FeaturesModuleUpdateInput = Required<
  Pick<
    JobsiteFeaturesModuleInput,
    | 'announcementsEnabled'
    | 'archivingAccessEventsEnabled'
    | 'formsEnabled'
    | 'addWorkersToDailyLog'
    | 'doNotAllowToSkipSelfOnboardingSteps'
    | 'enforceValidContractorInSelfOnboarding'
    | 'workerAppEnabled'
    | 'workerAppSelfCheckInEnabled'
    | 'workerAppAdminCheckInEnabled'
    | 'workerAppBadgeScanningEnabled'
    | 'workerAppKioskCheckInEnabled'
    | 'schedulingEnabled'
    | 'selfOnboardingAutoCompleteEnabled'
    | 'workerAppKioskFrontCameraEnabled'
  >
>;

type FeaturesSectionUpdateInput = Pick<
  JobsiteUpdateInput,
  'schedulerAccountId' | 'featuresModule'
>;

export const getFeaturesModuleInput = (
  jobsite: Jobsite,
  features: EditJobsiteConfigurationFormData['features'],
  dirtyFields?: DeepMap<EditJobsiteConfigurationFormData['features'], true>,
): JobsiteUpdateInput['featuresModule'] => {
  if (!dirtyFields) return undefined;

  const getUpdateInputValue = getUpdateInputValueFunction(
    features,
    dirtyFields,
  );
  const getDailyLogUpdateInputValue = getUpdateInputValueFunction(
    features.dailyLog,
    dirtyFields?.dailyLog,
  );
  const getSchedulingUpdateValue = getUpdateInputValueFunction(
    features.scheduling,
    dirtyFields?.scheduling,
  );

  const hasFeatureModuleChanged = Object.keys(dirtyFields ?? {}).some((key) =>
    key !== 'scheduling'
      ? dirtyFields[key as keyof typeof dirtyFields]
      : dirtyFields.scheduling?.schedulingEnabled,
  );

  if (hasFeatureModuleChanged) {
    const featuresModule = ensureNonUndefinedFields<FeaturesModuleUpdateInput>({
      announcementsEnabled: getUpdateInputValue('announcementsEnabled'),
      archivingAccessEventsEnabled: getUpdateInputValue(
        'archivingAccessEventsEnabled',
      ),
      formsEnabled: getDailyLogUpdateInputValue('formsEnabled'),
      addWorkersToDailyLog: getDailyLogUpdateInputValue('addWorkersToDailyLog'),
      doNotAllowToSkipSelfOnboardingSteps: getUpdateInputValue(
        'doNotAllowToSkipSelfOnboardingSteps',
      ),
      enforceValidContractorInSelfOnboarding: getUpdateInputValue(
        'enforceValidContractorInSelfOnboarding',
      ),
      workerAppEnabled: getUpdateInputValue('workerAppEnabled'),
      workerAppSelfCheckInEnabled: getUpdateInputValue(
        'workerAppSelfCheckInEnabled',
      ),
      workerAppAdminCheckInEnabled: getUpdateInputValue(
        'workerAppAdminCheckInEnabled',
      ),
      workerAppBadgeScanningEnabled: getUpdateInputValue(
        'workerAppBadgeScanningEnabled',
      ),
      workerAppKioskCheckInEnabled: getUpdateInputValue(
        'workerAppKioskCheckInEnabled',
      ),
      schedulingEnabled: getSchedulingUpdateValue('schedulingEnabled'),
      selfOnboardingAutoCompleteEnabled: getUpdateInputValue(
        'selfOnboardingAutoCompleteEnabled',
      ),
      workerAppKioskFrontCameraEnabled: getUpdateInputValue(
        'workerAppKioskFrontCameraEnabled',
      ),
    });

    const moduleName = `${jobsite.name} - features`;
    return {
      name: moduleName,
      slug: slugify(moduleName, { lower: true, trim: true }),
      isEnabled: true,
      ...featuresModule,
    };
  }
  return undefined;
};

export const getFeaturesSectionUpdateInput = (
  jobsite: Jobsite,
  features: EditJobsiteConfigurationFormData['features'],
  dirtyFields?: DeepMap<EditJobsiteConfigurationFormData['features'], true>,
): FeaturesSectionUpdateInput => {
  const getSchedulingUpdateValue = getUpdateInputValueFunction(
    features.scheduling,
    dirtyFields?.scheduling,
  );

  return {
    featuresModule: getFeaturesModuleInput(jobsite, features, dirtyFields),
    schedulerAccountId: getSchedulingUpdateValue('schedulerAccountId'),
  };
};
